/**
 * Which book the CRM page is showing — and the URL that names it.
 *
 * A one-line module because three files need the same vocabulary and none of them should
 * own it: the page parses the URL, the tabs render it, and the workspace queries with it.
 *
 * ## The agency view is DEEP-LINKABLE, and that is the point of putting it in the URL
 *
 * The first thing an agency owner does with "I can finally see the whole office book" is
 * send the link to their partner. `?view=org` (with `&org=<id>` for a realtor in more
 * than one agency) survives that, survives a reload, and survives the back button — none
 * of which a `useState` toggle does. It is the same reason `?contact=` already exists on
 * this page.
 *
 * The org id in the URL grants NOTHING. Scope is resolved server-side from
 * `dashboard-orgs` on every request, so a pasted link naming an agency the reader is not
 * in is a 403, not a leak — which is exactly why it is safe to put there.
 */

export type CrmContactsView = "mine" | "org";

/** The default, and what any unrecognised value falls back to. */
export const DEFAULT_CRM_VIEW: CrmContactsView = "mine";

export const CRM_VIEW_PARAM = "view";
export const CRM_ORG_PARAM = "org";

/**
 * Parses `?view=`, defaulting to the caller's own book.
 *
 * ⚠️ It narrows to `mine` for anything it does not recognise, and that direction matters:
 * an unknown string must never select the WIDER scope. The server enforces the same rule
 * (`parseCrmScopeView` throws rather than falling through to the org branch) — this is the
 * client half of it, so a typo'd link renders the safe page instead of a 403.
 */
export const parseCrmView = (raw: string | null | undefined): CrmContactsView =>
  raw === "org" ? "org" : DEFAULT_CRM_VIEW;

/**
 * WHICH AGENCY A MULTI-AGENCY REALTOR LANDS ON, absent a choice in the URL.
 *
 * ⚠️ THIS WAS `orgs[0]`, AND IT LOCKED PEOPLE OUT. Permissions are per agency, so the same
 * person is routinely an admin of one and a plain member of another — and `member.browse`
 * is false by default, which hides the agency tab. Picking blindly therefore decided,
 * on directory ordering, whether the feature existed at all: measured on a real account
 * (2 agencies — admin of one, member of the other), landing on the member one hid the tab
 * completely. The org PICKER lived inside those tabs, so there was no way back.
 *
 * Preferring an agency the caller ADMINISTERS is not a permission decision — the server
 * still resolves what they may do there — it is a decision about which of several correct
 * answers to open on, and "the one you run" is the one that has something to show. It
 * reads `isAdmin` off the directory, so it costs no request.
 *
 * It is only a DEFAULT: `?org=` always wins, so a link naming an agency still opens it.
 */
export const preferredCrmOrgId = (orgs: readonly { id: string; isAdmin: boolean }[]): string | null =>
  orgs.find((org) => org.isAdmin)?.id ?? orgs[0]?.id ?? null;

/**
 * The agency every request on the page names: the caller's choice when it is real, the
 * preferred default otherwise.
 *
 * ⚠️ THE MEMBERSHIP CHECK IS THE OTHER HALF, and it matters most for a SINGLE-agency
 * realtor. The choice persists in `?org=` and in `localStorage`, so an id left behind by a
 * membership that has since ended — or by a link from a colleague in a different agency —
 * pins the page to an org that 403s every request, on every reload, with no picker on
 * screen to correct it (there is only one agency, so there is nothing to pick between). An
 * id the directory does not list cannot be right, so it is dropped rather than sent.
 *
 * Returns `null` while the directory is still loading, which is correct: naming nothing is
 * what the server answers `CRM_SCOPE_AMBIGUOUS` to, and the page renders its narrow,
 * pre-agency self until the answer arrives.
 */
export const resolveCrmOrgId = (
  chosen: string | null | undefined,
  orgs: readonly { id: string; isAdmin: boolean }[]
): string | null => {
  const named = chosen && orgs.some((org) => org.id === chosen) ? chosen : null;
  return named ?? preferredCrmOrgId(orgs);
};

/**
 * Remembering the last agency across sessions.
 *
 * Only meaningful for the handful of realtors in more than one, and deliberately NOT
 * remembering the VIEW: coming back tomorrow to the whole agency book when you opened
 * your own yesterday would be a page that changed under you. The agency view is where a
 * link puts you, not where you land by default.
 */
export const CRM_LAST_ORG_STORAGE_KEY = "crmLastOrgId";
